import React from 'react';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { QRCodeSVG } from 'qrcode.react';
import { Copy, Check, Minus, Image as ImageIcon } from 'lucide-react';

// ===== Shared Wizard Building Blocks =====

export const FormField = ({ label, required, error, children }) => (
  <div className="space-y-1">
    <Label className="text-sm">{label}{required && ' *'}</Label>
    {children}
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

export const FormSelect = ({ value, onValueChange, placeholder, options, error, disabled, testId }) => (
  <Select value={value || ''} onValueChange={onValueChange} disabled={disabled}>
    <SelectTrigger className={error ? 'border-red-500' : ''} data-testid={testId}><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
  </Select>
);

export const OrangeCheckbox = ({ id, checked, indeterminate, onChange, label, disabled, testId }) => (
  <label htmlFor={id} className={`inline-flex items-center gap-2 cursor-pointer select-none ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
    <span
      className={`flex items-center justify-center h-4 w-4 rounded border-2 transition-colors shrink-0 ${
        checked || indeterminate ? 'bg-orange-500 border-orange-500' : 'bg-white border-slate-300 hover:border-orange-400'
      } ${disabled ? '' : 'focus-within:ring-2 focus-within:ring-orange-500/30'}`}
    >
      {checked && !indeterminate && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
      {indeterminate && <Minus className="h-3 w-3 text-white" strokeWidth={3} />}
    </span>
    <input
      type="checkbox" id={id} checked={checked} onChange={onChange} disabled={disabled}
      className="sr-only" data-testid={testId}
    />
    {label && <span className="text-sm text-slate-700">{label}</span>}
  </label>
);

export const MultiSelectCheckboxes = ({ label, options, selected, onChange, testId }) => (
  <div className="space-y-1" data-testid={testId}>
    <Label className="text-sm">{label}</Label>
    <div className="flex flex-wrap gap-3 mt-1.5">
      {options.map(opt => {
        const checked = selected.includes(opt);
        const optId = `${testId}-${opt.replace(/\s/g, '-').toLowerCase()}`;
        return (
          <OrangeCheckbox
            key={opt} id={optId} checked={checked}
            onChange={() => onChange(checked ? selected.filter(s => s !== opt) : [...selected, opt])}
            label={opt} testId={optId}
          />
        );
      })}
    </div>
  </div>
);

export const ReviewSection = ({ title, rows }) => (
  <div className="mb-3">
    <h5 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">{title}</h5>
    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-sm">
      {rows.map(([label, value]) => (
        <React.Fragment key={label}>
          <span className="text-slate-500">{label}:</span>
          <span className="font-medium text-slate-800">{value || '-'}</span>
        </React.Fragment>
      ))}
    </div>
  </div>
);

export const WizardStepper = ({ steps, currentStep, onStepClick }) => (
  <div className="flex items-center gap-0.5 mt-3" data-testid="wizard-progress">
    {steps.map((s) => (
      <div key={s.id} className="flex-1 relative group" data-testid={`step-indicator-${s.id}`}>
        <button
          type="button"
          onClick={() => onStepClick(s.id)}
          className={`w-full h-2 rounded-full transition-colors cursor-pointer ${s.id <= currentStep ? 'bg-orange-500 hover:bg-orange-600' : 'bg-slate-200 hover:bg-slate-300'}`}
          data-testid={`step-nav-${s.id}`}
        />
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-slate-800 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
          Step {s.id} of {steps.length}: {s.title}
        </div>
      </div>
    ))}
  </div>
);

const PUBLIC_BASE_URL = process.env.REACT_APP_PUBLIC_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : '');
export const buildQrUrl = (qrToken) => `${PUBLIC_BASE_URL}/public/qr/${qrToken}`;

export const QRPreviewModal = ({ open, onClose, qrToken, qrValue, productName }) => {
  const [copied, setCopied] = React.useState(false);
  const qrUrl = qrValue || (qrToken ? buildQrUrl(qrToken) : '');
  const handleCopy = () => {
    navigator.clipboard.writeText(qrUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm" data-testid="qr-preview-modal">
        <DialogHeader>
          <DialogTitle className="text-base">{productName || 'Product'} — QR Code</DialogTitle>
          <DialogDescription>Scan or share this QR code</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center py-4 gap-3">
          {qrUrl ? <QRCodeSVG value={qrUrl} size={200} level="L" /> : <div className="w-[200px] h-[200px] bg-slate-100 rounded" />}
          {qrUrl && <p className="text-[11px] text-slate-400 text-center max-w-[220px] break-all select-all">{qrUrl}</p>}
        </div>
        <div className="flex justify-end gap-2 border-t pt-3">
          {qrUrl && (
            <Button variant="outline" size="sm" onClick={handleCopy} data-testid="qr-copy-link-btn">
              {copied ? <><Check className="h-3.5 w-3.5 mr-1.5" />Copied</> : <><Copy className="h-3.5 w-3.5 mr-1.5" />Copy Link</>}
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose} data-testid="qr-modal-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// --- Review Layout Variant ---
// Change to 'inline' to instantly revert to legacy side-by-side layout (5-second rollback)
export const REVIEW_LAYOUT = 'banner';

export const ReviewProductHeader = ({ imagePreview, qrToken, infoRows, testIdPrefix = '', hideInfoSection = false }) => {
  const pfx = testIdPrefix ? `${testIdPrefix}-` : '';

  if (REVIEW_LAYOUT === 'banner') {
    return (
      <>
        <div className="flex items-center gap-4 pb-3">
          {imagePreview ? (
            <img src={imagePreview} alt="Product" className="w-16 h-16 rounded-lg object-cover border border-slate-200 shrink-0" data-testid={`${pfx}review-product-image`} />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center border border-slate-200 shrink-0" data-testid={`${pfx}review-product-image-placeholder`}>
              <ImageIcon className="h-5 w-5 text-slate-400" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm text-slate-900 truncate">{infoRows?.[0]?.[1] || '—'}</p>
            {infoRows?.[1]?.[1] && (
              <p className="text-xs text-slate-500 truncate">
                {infoRows[1][1]}{infoRows[2]?.[1] ? ` · ${infoRows[2][1]}` : ''}
              </p>
            )}
          </div>
          {qrToken && (
            <div className="flex flex-col items-center shrink-0" data-testid={`${pfx}review-qr-code`}>
              <QRCodeSVG value={buildQrUrl(qrToken)} size={56} level="L" />
              <p className="text-[8px] text-slate-400 mt-1">Specard QR</p>
            </div>
          )}
        </div>
        {!hideInfoSection && (
          <div className="pt-3">
            <ReviewSection title="Product Information" rows={infoRows} />
          </div>
        )}
      </>
    );
  }

  // inline (legacy) — image + ReviewSection + QR side-by-side
  return (
    <div className="flex gap-4 pb-3">
      {imagePreview ? (
        <img src={imagePreview} alt="Product" className="w-20 h-full min-h-[64px] max-h-[96px] rounded-lg object-cover border border-slate-200 shrink-0 self-stretch" data-testid={`${pfx}review-product-image`} />
      ) : (
        <div className="w-20 min-h-[64px] rounded-lg bg-slate-200 flex items-center justify-center border border-slate-200 shrink-0 self-stretch" data-testid={`${pfx}review-product-image-placeholder`}>
          <ImageIcon className="h-6 w-6 text-slate-400" />
        </div>
      )}
      <div className="flex-1">
        <ReviewSection title="Product Information" rows={infoRows} />
      </div>
      {qrToken && (
        <div className="flex flex-col items-center shrink-0" data-testid={`${pfx}review-qr-code`}>
          <QRCodeSVG value={buildQrUrl(qrToken)} size={64} level="L" />
          <p className="text-[8px] text-slate-400 mt-1">Specard QR</p>
        </div>
      )}
    </div>
  );
};

